import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { siteInfo, milestones } from '@/mocks/site';

export default function AboutPage() {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const organizationSchema = {
    name: siteInfo.name,
    url: 'https://www.91pian.com',
    description: siteInfo.description,
    foundingDate: siteInfo.foundedYear,
    email: siteInfo.email,
    telephone: siteInfo.phone,
    address: {
      '@type': 'PostalAddress',
      streetAddress: '建国路88号SOHO现代城A座',
      addressLocality: '北京',
      addressRegion: '朝阳区',
      addressCountry: 'CN',
    },
    sameAs: ['https://weibo.com/91pian'],
  };

  const webPageSchema = {
    name: `关于我们 - ${siteInfo.name}`,
    description: `了解${siteInfo.name}的品牌故事、发展历程与核心优势。${siteInfo.description}`,
    url: 'https://www.91pian.com/about',
    inLanguage: 'zh-CN',
    isPartOf: { '@type': 'WebSite', name: siteInfo.name, url: 'https://www.91pian.com' },
  };

  return (
    <>
      <SchemaOrg type="Organization" data={organizationSchema} />
      <SchemaOrg type="WebPage" data={webPageSchema} />

      <div className="h-16" />
      <Navbar />

      <main className="min-h-screen bg-background-50">
        {/* Hero */}
        <section className="relative w-full h-[300px] md:h-[380px] overflow-hidden flex items-center justify-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20media%20company%20office%20with%20warm%20lighting%2C%20sleek%20design%2C%20creative%20workspace%20atmosphere%2C%20film%20posters%20on%20walls%2C%20professional%20environment%2C%20cinematic%20aesthetic%2C%20golden%20and%20warm%20amber%20tones&width=1920&height=800&seq=about-hero-91&orientation=landscape"
              alt="关于91片"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/60 to-black/80" />
          </div>
          <div className="relative z-10 text-center px-4">
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
              关于91片
            </h1>
            <p className="mt-3 text-white/70 text-sm md:text-base max-w-lg mx-auto">
              致力打造国内领先的视频内容平台
            </p>
          </div>
        </section>

        {/* Breadcrumb */}
        <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
          <Breadcrumb
            items={[
              { name: '首页', href: '/' },
              { name: '关于我们' },
            ]}
          />
        </div>

        {/* Brand Intro */}
        <section className="w-full max-w-7xl mx-auto px-4 md:px-6 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                品牌故事
              </h2>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                {siteInfo.name}创立于{siteInfo.foundedYear}年，是国内最早一批专注于视频内容聚合与分发的平台之一。从最初的几十部影片起步，到如今拥有超过{siteInfo.totalContent.toLocaleString()}+部正版高清内容，91片始终坚持以用户体验为核心，不断优化技术和服务。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                我们深知，在信息爆炸的时代，用户最需要的是一个简单、快速、安全的内容获取渠道。因此，91片团队持续投入资源进行技术创新，打造了极速播放引擎和智能推荐系统，让每一位用户都能在最短的时间内找到并观看自己喜欢的内容。
              </p>
              <p className="mt-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                截止目前，91片已累计服务超过{siteInfo.totalUsers.toLocaleString()}注册用户，日活跃用户超过{siteInfo.dailyActive.toLocaleString()}，成为国内最受欢迎的视频内容平台之一。
              </p>
            </div>
            <div className="aspect-square rounded-lg overflow-hidden bg-background-200">
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20streaming%20platform%20brand%20identity%20visual%20with%20warm%20cinematic%20lighting%2C%20minimalist%20design%2C%20content%20library%20concept%2C%20digital%20media%20aesthetic%2C%20golden%20amber%20tones%2C%20professional%20tech%20company%20photography&width=800&height=800&seq=about-brand&orientation=squarish"
                alt="91片品牌形象"
                className="w-full h-full object-cover object-top"
              />
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-16 bg-white">
          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              {[
                { value: '5000万+', label: '注册用户' },
                { value: '20万+', label: '正版内容' },
                { value: '800万', label: '日活跃用户' },
                { value: siteInfo.foundedYear.toString(), label: '创立年份' },
              ].map((stat, idx) => (
                <div key={idx}>
                  <p className="font-heading font-bold text-3xl md:text-4xl text-primary-500">
                    {stat.value}
                  </p>
                  <p className="mt-2 text-sm text-foreground-600">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Milestones */}
        <section className="py-16 md:py-20 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 text-center mb-12">
              发展历程
            </h2>
            <div className="relative">
              {/* Timeline Line */}
              <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-background-300 md:-translate-x-px" />

              <div className="flex flex-col gap-8">
                {milestones.map((ms, idx) => (
                  <div
                    key={idx}
                    className={`relative flex items-start gap-6 ${
                      idx % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                    }`}
                  >
                    {/* Dot */}
                    <div className="absolute left-4 md:left-1/2 w-3 h-3 rounded-full bg-primary-500 border-2 border-background-50 transform -translate-x-1/2 z-10" />

                    {/* Content Wrapper */}
                    <div className={`flex-1 ${idx % 2 === 0 ? 'md:text-right md:pr-12' : 'md:text-left md:pl-12'} pl-12 md:pl-0`}>
                      <div className="inline-block bg-white rounded-lg p-4 md:p-5 border border-background-200">
                        <span className="text-xs font-bold text-primary-500">{ms.year}</span>
                        <h3 className="font-heading font-semibold text-foreground-950 text-sm md:text-base mt-1">
                          {ms.title}
                        </h3>
                        <p className="text-xs md:text-sm text-foreground-600 mt-1">{ms.description}</p>
                      </div>
                    </div>

                    {/* Empty space for alternating layout on desktop */}
                    <div className="hidden md:block flex-1" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section id="contact" className="py-16 md:py-20 bg-white">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950">
                联系我们
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                有任何问题或合作意向，欢迎随时联系我们
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center p-6 rounded-lg bg-background-50">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-3">
                  <i className="ri-mail-line text-primary-500 text-xl" />
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">邮箱</h3>
                <p className="text-sm text-foreground-600">{siteInfo.email}</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-3">
                  <i className="ri-phone-line text-primary-500 text-xl" />
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">电话</h3>
                <p className="text-sm text-foreground-600">{siteInfo.phone}</p>
              </div>
              <div className="text-center p-6 rounded-lg bg-background-50">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-primary-100 mb-3">
                  <i className="ri-map-pin-line text-primary-500 text-xl" />
                </div>
                <h3 className="font-heading font-semibold text-foreground-950 text-sm mb-1">地址</h3>
                <p className="text-sm text-foreground-600">{siteInfo.address}</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}